import {
  LinearEncoding,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  MeshToonMaterial,
  Object3D,
  Plane,
  Shader,
  sRGBEncoding,
  TextureLoader,
  Vector3,
} from "three";
import { Context } from "../../poser/context";

import { GPULevel } from "../../poser/util/benchmark";

import {
  colorStringToVector,
  MaterialColors,
  MaterialDetails,
  MaterialDetailsFromArray,
} from "./details";

/**
 * Subschema for materials files - set of texture filenames that define
 * a material.
 */
interface TextureURIs {
  base: string;
  normal: string;
  packed: string;
  mask?: string;
  tints?: string[];
}

/**
 * Material file schema
 */
export interface MaterialTexturesMetadata {
  [key: string]: TextureURIs;
}

interface MaterialDetailsSet {
  material: Material;
  details?: MaterialDetails;
}

/**
 * Collection of TextureURIs (material definitions) defined in a json file for
 * each team (and umpires). Can also be parsed from a GLTF file. There is one
 * TextureURIs for each of the 6 materials, which for the current master GLTF
 * are:
 *
 * Jersey_Top, CatcherGear, Gear, Jersey_Bot, UniformAccessories & Skin
 *
 * When applying, we traverse the meshes in the passed Object3D, look for the material.name
 * in textureMap and get a TextureURIs (set of texture filenames that describe a material)
 * If we don't find a three.Material in the materialCache for any of TextureURIs in textureMap,
 * then we make one:
 * - load the new texture(s)
 * - set the corresponding `map` objects in the meshes material to the right texture
 *   from TextureURIs
 * - cache the mesh's material in the materialCache for other MaterialTextures to use
 */
export class MaterialTextures {
  static staticMaterialCache: Map<string, MaterialDetailsSet> = new Map();

  static clippingPlane = new Plane(new Vector3(0, 1, 0));

  // map for material.name -> TextureURI (defines what this MaterialTexture is)
  private loader = new TextureLoader();

  private textureMap: Map<string, TextureURIs> = new Map();
  private details?: MaterialDetails;

  private highPerfGpu: boolean;

  private basePath: string | undefined = undefined;

  constructor(context: Context) {
    this.basePath = `${context.options.assetBase}/models/generic/`;
    this.highPerfGpu = context.options.gpuLevel == GPULevel.HIGH;
  }

  /**
   * Apply the textureMap to a GLTF object
   * @param target GLTF models to change the materials of
   */
  apply(targets: Object3D[]): void {
    const meshes = new Set<Mesh>();
    for (const target of targets) {
      target.traverse((object) => {
        const mesh = object as Mesh;
        if (!mesh.isMesh || !mesh.material) {
          return;
        }

        meshes.add(mesh);
      });
    }

    for (const mesh of meshes) {
      if (Array.isArray(mesh.material)) {
        console.error("material arrays unsupported");
        continue;
      }

      const textureDetails = this.textureMap.get(mesh.material.name);
      if (!textureDetails) {
        console.error(`${mesh.name}: no texture map for ${mesh.material.name}`);
        return;
      }

      const materialUid = materialToString(textureDetails);

      const materialCache = MaterialTextures.staticMaterialCache;

      let cachedMat = materialCache.get(materialUid)?.material;
      let cachedDetails = materialCache.get(materialUid)?.details;

      if (cachedDetails) {
        this.details = cachedDetails;
      }

      if (cachedMat) {
        mesh.material = cachedMat;
      } else {
        const matName = mesh.material.name;

        const oldMat = mesh.material as MeshStandardMaterial;
        if (!oldMat.isMeshStandardMaterial) {
          console.error("non standard material used");
        }

        const highPerf = this.highPerfGpu;
        const matType = highPerf ? MeshStandardMaterial : MeshBasicMaterial;

        mesh.material = new matType({
          clippingPlanes: [MaterialTextures.clippingPlane],
          clipShadows: true,
        });
        mesh.material.name = matName;
        // cast to standard, even though it might be toon
        const cachedMatStandard = mesh.material as MeshStandardMaterial;

        cachedMat = cachedMatStandard;

        // make material
        let texType: keyof TextureURIs;
        for (texType in textureDetails) {
          const uriDetails = textureDetails[texType];
          if (!uriDetails) {
            console.error(`missing texture name ${cachedMat.name}`);
            continue;
          }

          if (Array.isArray(uriDetails)) {
            // ignore "tints" entry
            continue;
          }

          const texFilename: string = uriDetails;

          // change texture
          const tex = this.loader.load(this.basePath + texFilename);
          tex.encoding = LinearEncoding;
          tex.flipY = false;
          if (tex) {
            switch (texType) {
              case "base":
                tex.encoding = sRGBEncoding;
                cachedMatStandard.map = tex;
                break;

              case "normal":
                cachedMatStandard.normalMap = tex;
                break;

              case "packed":
                cachedMatStandard.aoMap = tex;
                if (highPerf) cachedMatStandard.metalnessMap = tex;
                if (highPerf) cachedMatStandard.roughnessMap = tex;
                break;

              case "mask": {
                if (cachedMatStandard.userData.shader) {
                  console.error(
                    `shader already augmented for ${texFilename}`,
                    cachedMatStandard.onBeforeCompile
                  );
                  break;
                }

                const tints = textureDetails.tints;
                if (!tints) {
                  console.error(
                    `no tints defined for ${texFilename}`,
                    textureDetails
                  );
                  break;
                }

                const details = MaterialDetailsFromArray(tints);

                // assume that the Gear colors define team colors
                if (matName == "Gear") {
                  this.details = details;
                  cachedDetails = details;
                }

                cachedMatStandard.onBeforeCompile = (shader: Shader) => {
                  const tintColors = details?.colors;
                  if (!tintColors) {
                    return;
                  }

                  shader.uniforms.mask = { value: tex };
                  shader.uniforms.maskColorR = {
                    value: colorStringToVector(tintColors.primary),
                  };
                  shader.uniforms.maskColorG = {
                    value: colorStringToVector(tintColors.secondary),
                  };
                  shader.uniforms.maskColorB = {
                    value: colorStringToVector(tintColors.tertiary),
                  };
                  shader.uniforms.maskColorA = {
                    value: colorStringToVector(tintColors.quaternary),
                  };

                  const declarations =
                    "uniform sampler2D mask;\n" +
                    "uniform vec4 maskColorR;\n" +
                    "uniform vec4 maskColorG;\n" +
                    "uniform vec4 maskColorB;\n" +
                    "uniform vec4 maskColorA;\n";

                  shader.fragmentShader = declarations + shader.fragmentShader;
                  shader.fragmentShader = shader.fragmentShader.replace(
                    "#include <color_fragment>",
                    `
                      vec4 sampledMask = texture2D( mask, vUv );

                      vec4 color = vec4( 1.0 );

                      color = mix( color, maskColorR, sampledMask.r );
                      color = mix( color, maskColorG, sampledMask.g );
                      color = mix( color, maskColorB, sampledMask.b );
                      color = mix( color, maskColorA, ( 1.0 - sampledMask.a ) );

                      diffuseColor *= color;

                    `
                  );

                  cachedMatStandard.userData.shader = shader;
                };

                break;
              }

              default:
                break;
            }

            cachedMatStandard.needsUpdate = true;
          } else {
            console.error(
              `unable to load texture ${this.basePath + texFilename}`
            );
          }
        }

        // cache material for other actors
        materialCache.set(materialUid, {
          material: cachedMat,
          details: cachedDetails,
        });
      }
    }
  }

  getColors(): MaterialColors | undefined {
    // we assume that Gear colors represent colour order for team
    return this.details?.colors;
  }

  /**
   * Parse textureMap from GLTF file directly (only used in outfitter and for debug
   * currently)
   * @param data GLTF json object
   * @returns this post parse
   */
  fromGLTF(data: any): MaterialTextures {
    const map = this.textureMap;

    const textures = data.textures;
    const images = data.images;
    const materials = data.materials;
    const meshes = data.meshes;
    const nodes = data.nodes;

    if (!textures || !materials || !meshes || !nodes || !images) {
      console.error("incomplete gltf data");
      return this;
    }

    try {
      for (const node of nodes) {
        if (node.mesh) {
          const mesh = meshes[node.mesh];
          if (mesh.primitives) {
            if (mesh.primitives.length == 1) {
              const index = mesh.primitives[0].material;
              const material = materials[index];
              if (material) {
                const name = material.name;
                const existing = map.get(name);
                const skin: TextureURIs =
                  existing ||
                  ({
                    normal:
                      images[textures[material?.normalTexture?.index]?.source]
                        .uri,
                    packed:
                      images[
                        textures[material?.occlusionTexture?.index]?.source
                      ].uri,
                    base: images[
                      textures[
                        material?.pbrMetallicRoughness?.baseColorTexture?.index
                      ]?.source
                    ].uri,
                  } as TextureURIs);

                if (!map.has(name)) {
                  map.set(name, skin);
                }
              }
            } else {
              console.warn(
                `mesh ${node.name} has ${mesh.primitives.length} primitives`
              );
            }
          }
        }
      }
    } catch (e) {
      console.error("problem parsing gltf materials", e);
      map.clear();
    }

    return this;
  }

  // TODO: all common entries (masks, common textures, etc) out into "Base" material json
  /**
   * Parse textureMap from json file
   * @param data json data of form MaterialTexturesMetadata
   * @returns this post parse
   */
  fromJSON(data: MaterialTexturesMetadata) {
    this.textureMap.clear();
    for (const name in data) {
      const uris = data[name];

      this.textureMap.set(name, uris);
    }

    return this;
  }

  /**
   * Serialise to MaterialTexturesMetadata json string
   * @returns
   */
  toJSON(): string {
    const obj: MaterialTexturesMetadata = {};

    for (const [k, v] of this.textureMap) {
      obj[k] = v;
    }

    return JSON.stringify(obj, null, 2) + "\n";
  }
}

/**
 * Stringify function for TextureURIs - used to key the materialCache
 * @param entry
 * @returns uid string for TextureURIs
 */
function materialToString(entry: TextureURIs): string {
  return (
    entry.base +
    "-" +
    entry.normal +
    "-" +
    entry.packed +
    (entry.mask ? `-${entry.mask}-${entry.tints}` : "")
  );
}
